import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import AppLayoutWrapper from "./components/Layout/AppLayoutWrapper";
import RenderOnAnonymous from "./components/KeycloakComponents/RenderOnAnonymous";
import RenderOnAuthenticated from "./components/KeycloakComponents/RenderOnAuthenticated";
import Login from "./components/KeycloakComponents/Login";
import DedicatedClusters from "./pages/DedicatedClusters";
import SharedDeployments from "./pages/SharedDeployments";
import InviteMember from "./pages/InviteMember";
import OrganizationsList from "./pages/OrganizationsList";
import OrganizationDetails from "./pages/OrganizationDetails";
import PendingInvitations from "./pages/PendingInvitations";
import OrganizationDomain from "pages/OrganizationDomains";
import RenderOnRole from "components/KeycloakComponents/RenderOnRole";
import OrganizationSettings from "pages/organizationSettingsPage";
import EditRoles from "pages/EditRoles";
import { NotificationProvider } from "./contexts/NotificationContext";
import AccountSettings from "pages/AccountSettings";
import SSOProviders from "pages/SSOProvidersPage";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext";
import { UserProvider } from "contexts/UserContext";
import PERMISSIONS from "./configs/Permissions";
import ContactPage from "pages/ContactPage";
import { HelpPanelProvider } from "contexts/HelpPanelContext";
import CreateDedicatedClusterPage from "components/deployments/CreateDedicatedClusterPage";
import CreateSharedDeploymentPage from "components/deployments/CreateSharedDeploymentPage";
import Dashboard from "pages/Dashboard";
import ExtensionsPage from "pages/ExtensionsPage";
import { ClusterProvider } from "contexts/ClusterContext";
import DedicatedClusterDetails from "pages/DedicatedClusterDetails";


const App: React.FC = () => {
  return (
    <UserProvider>
      <BreadcrumbProvider>
        <NotificationProvider>
          <ClusterProvider>
            <Router>
              <HelpPanelProvider>
                <RenderOnAnonymous>
                  <Login />
                </RenderOnAnonymous>

                <RenderOnAuthenticated>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                    <Route element={<AppLayoutWrapper />}>
                      <Route
                        path="dashboard"
                        element={<RenderOnRole
                          roles={[PERMISSIONS.DEPLOYMENTS.VIEW_CLUSTERS]}
                          showNotAllowed={true}
                        >
                          <Dashboard />
                        </RenderOnRole>}
                      />

                      <Route
                        path="dedicated-clusters"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.VIEW_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <DedicatedClusters />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="dedicated-clusters/:clusterId"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.VIEW_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <DedicatedClusterDetails />
                          </RenderOnRole>
                        }
                      />
                      <Route path="dedicated-clusters/:clusterId/extensions" 
                      element={
                      <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.MANAGE_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <ExtensionsPage />
                          </RenderOnRole> 
                      }
                      />
                      <Route
                        path="dedicated-clusters/create"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.MANAGE_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <CreateDedicatedClusterPage />
                          </RenderOnRole>
                        }
                      />
                       {/* deactivated for now, as we removed shared deployments */}
                      {/* <Route
                        path="shared-deployments/create"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.MANAGE_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <CreateSharedDeploymentPage />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="shared-deployments"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.DEPLOYMENTS.VIEW_CLUSTERS]}
                            showNotAllowed={true}
                          >
                            <SharedDeployments />
                          </RenderOnRole>
                        }
                      /> */}
                      <Route
                        path="organizations"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.VIEW_ORGANIZATIONS]}
                            showNotAllowed={true}
                          >
                            <OrganizationsList />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.VIEW_ORGANIZATIONS]}
                            showNotAllowed={true}
                          >
                            <OrganizationDetails />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/invite"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS]}
                            showNotAllowed={true}
                          >
                            <InviteMember />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/settings"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.VIEW_ORGANIZATIONS]}
                            showNotAllowed={true}
                          >
                            <OrganizationSettings />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/invitations/pending"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS]}
                            showNotAllowed={true}
                          >
                            <PendingInvitations />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/edit-roles/:memberId"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS]}
                            showNotAllowed={true}
                          >
                            <EditRoles />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/domains"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS]}
                            showNotAllowed={true}
                          >
                            <OrganizationDomain />
                          </RenderOnRole>
                        }
                      />
                      <Route
                        path="organizations/:id/sso-providers"
                        element={
                          <RenderOnRole
                            roles={[PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS]}
                            showNotAllowed={true}
                          >
                            <SSOProviders />
                          </RenderOnRole>
                        }
                      />
                      <Route path="account-settings" element={<AccountSettings />} />
                      <Route path="contact" element={<ContactPage />} />
                    </Route>
                  </Routes>
                </RenderOnAuthenticated>
              </HelpPanelProvider>
            </Router>
          </ClusterProvider>
        </NotificationProvider>
      </BreadcrumbProvider>
    </UserProvider>
  );
};

export default App;

